// FEAT-050 S5a: N단계 quota ctx(nodesByCompanyId/parentOf/childrenOf) 조립 순수 헬퍼.
// 노드 레코드는 fan-out thunk 가 companyId 를 태깅해 전달(원장 sk 는 managerGroupId 기반이나 조회 시 companyId 앎).

import { getLicenseRole } from './licenseHelper';
import { ORG_HQ } from '../config/appConstants';
import { isCorpLedgerSk, isReceivedAllocationSk, mgOfAllocationSk } from './rayteamsTables';
// BUG-206 R3: 원장 자기소모 식은 집행 경로와 공유한다(사본 금지). 순환 없음 — smartDentQuota 는
// allocationGraph / licenseHelper / rayteamsTables 만 import 한다.
import { computeLedgerSelfConsumed } from './smartDentQuota';

// IMP-420 M2: sk 세그먼트 수로 "corp 원장(자식 redeem 롤업)" vs "받은 레코드(자기소모)" 를 구분.
// corp 원장(2-seg, `info:{mg}`)의 u 는 자기-mg 하위 리딤 롤업이라 그대로 자기소모로 쓸 수 없다.
// i 는 IssueQr corp 분기가 직접 ADD 하는 자기 발급 카운터이므로 corp 원장이어도 폐기하지 않고 기존
// additive 계약(base.i + Σ셀프딜링.i, 3-seg 와 동일)을 유지한다. 단 노드 `i_*` 를 **읽는 코드는 현재
// 0 건**이다(PR #543 R2 전수 확인) — 표시 참고치이며 quota 결정에는 쓰이지 않는다(BUG-206 에서
// over-issuance 게이트 `t−i−Σd` 폐기). 제거는 별도 작업. 3-seg 받은 레코드의 u 는 자기소모다.
// predicate 는 isCorpLedgerSk / isReceivedAllocationSk (SSoT: rayteamsTables.js, sk 포맷과 co-location).
//
// BUG-206 R2 (PR #543 리뷰 Major): 원장 u 를 **0 으로 폐기**하던 종전 규칙은 "자기소모는 셀프딜 행에만
// 있다" 는 가정이었다. IMP-425 로 셀프딜 행 생성이 폐지된 뒤에는 **원장 u 에만 자기 리딤이 있는 형태가
// 정상**이라 그 가정에서 자기소모가 통째로 소실됐다(표시·팝업 상한이 집행 천장보다 커짐 — 딜러 탭
// 자기 선택 발급에서 "입력은 되는데 INSUFFICIENT_QUOTA"). 집행 SSoT(`smartDentQuota.resolveSelfUsableCeiling`)
// 와 같은 식으로 정렬한다:
//
//   자기소모 = max( 원장u − Σ(같은 mg 의 다른 회사 3-seg 행.u),  Σ(자기 셀프딜 행.u) )
//
// BUG-206 R3 (PR #543 리뷰 Major): 식 자체는 `smartDentQuota.computeLedgerSelfConsumed` **공유 함수**가
// 갖는다 — 표시/집행 사본 2개를 두면 다음 판정 변경 때 한쪽만 갱신된다(사용자 지침: 계산식 이원화 금지).
// 이 파일은 그 함수에 넘길 **모집단**(같은 mg 의 타사 3-seg u 합, 자기 셀프딜 u 합)만 조립한다.
// 음수 폴백 warn·dedup 도 공유 함수 안에 있어 표시 경로에서도 관측된다.

// mg 별 3-seg 행 u 합계를 companyId 단위로 집계 — 원장 자기소모의 롤업 되빼기 모집단.
const build3SegURollup = (taggedRecords) => {
    const byMg = {};
    taggedRecords.forEach((r) => {
        if (!r || !r.companyId || !isReceivedAllocationSk(r.sk)) return;
        const mg = mgOfAllocationSk(r.sk);
        if (!mg) return;
        const slot = byMg[mg] = byMg[mg] || { total: {}, byCompany: {} };
        const own = slot.byCompany[r.companyId] = slot.byCompany[r.companyId] || {};
        ['u_initial', 'u_renewal'].forEach((f) => {
            slot.total[f] = (slot.total[f] ?? 0) + (r[f] ?? 0);
            own[f] = (own[f] ?? 0) + (r[f] ?? 0);
        });
    });
    return byMg;
};

// FEAT-050 (나) Increment 1: 같은 companyId 의 여러 레코드를 self-deal 흡수하여 노드 1개로 병합.
// base(받은 레코드/corp 원장, allocatedBy !== companyId)의 allocatedBy/t 는 그대로 유지하고,
// self-deal 레코드(allocatedBy === companyId)의 u_*/i_* 만 합산 흡수한다. base 가 없으면(비정상)
// 마지막 self-deal 을 노드로 사용(edge fallback). 레코드가 1개뿐이면 기존과 동일하게 원본 참조 유지.
export const buildNodesByCompanyId = (taggedRecords) => {
    if (!Array.isArray(taggedRecords)) return {};
    const rollup3Seg = build3SegURollup(taggedRecords);
    // 원장(2-seg) base 의 자기소모 — 식은 집행과 공유(computeLedgerSelfConsumed), 여기서는 모집단만 만든다.
    const ledgerSelfConsumed = (base, companyId, field, sumSelfDealU) => {
        const mg = mgOfAllocationSk(base?.sk);
        const slot = mg ? rollup3Seg[mg] : null;
        return computeLedgerSelfConsumed({
            ledgerU: base?.[field] ?? 0,
            othersU: (slot?.total?.[field] ?? 0) - (slot?.byCompany?.[companyId]?.[field] ?? 0),
            selfDealU: sumSelfDealU,
            warnCtx: { helper: 'buildNodesByCompanyId', companyId, field, recordSk: base?.sk },
        });
    };
    const groups = {};
    taggedRecords.forEach((r) => {
        if (r && r.companyId) {
            (groups[r.companyId] = groups[r.companyId] || []).push(r);
        }
    });
    const map = {};
    Object.keys(groups).forEach((companyId) => {
        // IMP-420 M3: mg-null 오염 row 로 같은 sk 가 base(allocatedBy:null)와 self-deal
        // (allocatedBy:cid)로 이중 태깅될 수 있다. sk 기준 dedup(첫 등장 유지)으로 진짜 중복만
        // 제거 — GetAllocationSubtree 가 base 를 먼저 push 하므로 base 가 남는다. 정상 self-deal
        // 은 sk 가 3-seg 로 corp 원장 base(2-seg)와 달라 dedup 대상이 아니다.
        const seen = new Set();
        let droppedCount = 0;
        const records = groups[companyId].filter((r) => {
            if (!r?.sk) return true;
            if (seen.has(r.sk)) {
                droppedCount += 1;
                return false;
            }
            seen.add(r.sk);
            return true;
        });
        // N4: sk dedup 도 silent-drop 금지(CLAUDE.md #7) 대상 — 1건 이상 드롭 시 warn.
        if (droppedCount > 0) {
            console.warn('[allocationContext] buildNodesByCompanyId: duplicate sk within companyId group — keeping first, dropping duplicates (mg-null contaminated row?)', {
                companyId,
                droppedCount,
            });
        }
        if (records.length === 1) {
            const only = records[0];
            // 단독 레코드가 corp 원장(2-seg)이면 u 는 mg 그룹 롤업이라 파일 헤더의 자기소모 식으로
            // 환산한다(셀프딜 행이 없으므로 하한항은 0). i 는 자기 발급 카운터라 원본 보존.
            map[companyId] = isCorpLedgerSk(only?.sk)
                ? {
                    ...only,
                    u_initial: ledgerSelfConsumed(only, companyId, 'u_initial', 0),
                    u_renewal: ledgerSelfConsumed(only, companyId, 'u_renewal', 0),
                }
                : only; // 무회귀: 원본 참조 그대로
            return;
        }
        const baseCandidates = records.filter((r) => r.allocatedBy !== companyId);
        const selfDeals = records.filter((r) => r.allocatedBy === companyId);
        // 비정상: 같은 companyId 에 받은 레코드(base)가 2개 이상 — 마지막만 채택하고 나머지는 폐기.
        // silent-drop 금지(CLAUDE.md #7): 데이터 정합성 이상 신호이므로 관측 가능한 warn 동반.
        if (baseCandidates.length > 1) {
            console.warn('[allocationContext] buildNodesByCompanyId: multiple base records for companyId — keeping last, dropping earlier', {
                companyId,
                baseCandidateCount: baseCandidates.length,
            });
        }
        if (selfDeals.length === 0) {
            map[companyId] = records[records.length - 1]; // self-deal 없음 → 기존 last-wins(참조 유지, 무회귀)
            return;
        }
        // base 후보가 여러 개면(비정상) 마지막 것 우선. 후보가 없으면 마지막 self-deal 을 edge fallback.
        const base = baseCandidates.length > 0
            ? baseCandidates[baseCandidates.length - 1]
            : selfDeals[selfDeals.length - 1];
        const absorbed = baseCandidates.length > 0 ? selfDeals : selfDeals.slice(0, -1);
        const sum = (field) => absorbed.reduce((acc, r) => acc + (r?.[field] ?? 0), 0);
        // base 가 corp 원장(2-seg)이면 u 는 mg 그룹 롤업이므로 파일 헤더의 자기소모 식으로 환산한다
        // (되빼기 + 셀프딜 하한 — `computeLedgerSelfConsumed`). i 는 corp 원장이어도 자기 발급
        // 카운터이므로 base.i + 셀프딜링 합(additive, 3-seg 와 동일)을 유지한다. base 가 3-seg 받은
        // 레코드면 u/i 모두 기존 계약대로 base + 셀프딜링 합.
        //
        // BUG-206 (PR #543 리뷰 Minor): `i_*` 흡수는 **표시 참고치**다 — 종전 근거였던 over-issuance
        // 발급 게이트(t−i−Σd)는 BUG-206 에서 폐기됐고, quota 결정(할당 상한·발급 천장)은 모두
        // `resolveSelfUsableCeiling`(u 기반)만 쓴다. `i` 를 다시 quota 항으로 되살리지 말 것.
        map[companyId] = isCorpLedgerSk(base.sk)
            ? {
                ...base,
                u_initial: ledgerSelfConsumed(base, companyId, 'u_initial', sum('u_initial')),
                u_renewal: ledgerSelfConsumed(base, companyId, 'u_renewal', sum('u_renewal')),
                i_initial: (base.i_initial ?? 0) + sum('i_initial'),
                i_renewal: (base.i_renewal ?? 0) + sum('i_renewal'),
            }
            : {
                ...base,
                u_initial: (base.u_initial ?? 0) + sum('u_initial'),
                u_renewal: (base.u_renewal ?? 0) + sum('u_renewal'),
                i_initial: (base.i_initial ?? 0) + sum('i_initial'),
                i_renewal: (base.i_renewal ?? 0) + sum('i_renewal'),
            };
    });
    return map;
};

export const buildParentOf = (companies) => {
    if (!Array.isArray(companies)) return {};
    const map = {};
    companies.forEach((c) => {
        if (c && c.companyId) map[c.companyId] = c.parentCompanyId;
    });
    return map;
};

export const buildChildrenOf = (companies) => {
    if (!Array.isArray(companies)) return {};
    const map = {};
    companies.forEach((c) => {
        if (c && c.companyId && c.parentCompanyId) {
            (map[c.parentCompanyId] = map[c.parentCompanyId] || []).push(c.companyId);
        }
    });
    return map;
};

// PR #546 R2(Architecture Major): 직속-자식 판정이 AllocateToDealer/RevokeFromDealer/IssueQr
// 세 곳에 같은 모양으로 인라인 복제되며 비교 축이 갈렸다(형제는 targetCompanyId 대
// corpId, IssueQr 는 targetCompanyId 대 actorCompanyId). 순수 함수로 통일하고 기준
// parentCompanyId 를 인자로 명시해 호출부에서 축 차이가 드러나게 한다.
// fail-closed: companies 미로드/대상 미발견 → false.
export const isDirectChildOf = (companies, targetCompanyId, parentCompanyId) => {
    const targetCompany = Array.isArray(companies)
        ? companies.find((c) => c?.companyId === targetCompanyId)
        : null;
    return !!targetCompany && targetCompany.parentCompanyId === parentCompanyId;
};

// FEAT-050 S5b: 할당/조회 인가 스코프 판정 — 대상이 actor 자신 또는 clist(회사 계층 자손) 안인가.
// skip-level 허용(가): 직속 아니어도 자손이면 true. clist 는 콤마 문자열/배열 모두 정규화.
export const isCompanyInScope = (targetCompanyId, actorCompanyId, clist) => {
    if (!targetCompanyId) return false;
    if (targetCompanyId === actorCompanyId) return true;
    const list = Array.isArray(clist)
        ? clist
        : (typeof clist === 'string' ? clist.split(',').map((s) => s.trim()) : []);
    return list.includes(targetCompanyId);
};

// FEAT-050 S5b M2: 조회(read) 스코프 인가 SSoT — HQ(su) 전사 bypass + clist 서브트리
// 판정을 캡슐화한다. PR #546 R2(리뷰 반영): 현재 유일한 소비처는 GetAllocationSubtree
// (read-only). write 경로(AllocateToDealer/RevokeFromDealer/IssueQr)는 clist 서브트리
// 보다 좁은 직속-자식 판정이 필요해 이 헬퍼를 쓰지 않고 `isDirectChildOf`(companies
// 기반) + 자기-판정을 조합한다 — write 재사용을 권하지 않는다.
// HQ 는 clist 가 전체 트리를 열거하지 못하는 환경(BUG-173, isHqUser 패턴) 대비
// 전사 권한으로 스코프 검사를 우회한다.
export const isAllocationActorInScope = (actor, targetCompanyId) => {
    if (getLicenseRole(actor) === ORG_HQ) return true;
    return isCompanyInScope(targetCompanyId, actor?.companyId ?? null, actor?.clist);
};

// BUG-196: 발행(issuance)은 할당(allocation)보다 엄격하다 — clist 자손 대행 발행을
// 허용하지 않고 self-dealing(자기 예산)만 허용한다. HQ(su)만 전사 bypass.
// 배경: 상위 조직이 하위 조직에 할당해 준 예산(budgetOwner=하위)을 대행 발행하던 갭
// (사용자 보고). isAllocationActorInScope 는 clist 자손을 통과시키므로 발행 경계로는
// 부적합. 발행은 budgetOwnerCompanyId === actor.companyId 일 때만 허용한다.
export const isIssuanceActorInScope = (actor, budgetOwnerCompanyId) => {
    if (getLicenseRole(actor) === ORG_HQ) return true;
    if (!budgetOwnerCompanyId) return false;
    return budgetOwnerCompanyId === (actor?.companyId ?? null);
};
